import React from 'react';
import type { PersonaBlock } from './featuresData';

const theme: Record<PersonaBlock['persona'], {
  section: string;
  eyebrow: string;
  headline: string;
  tileBorder: string;
  tileGlow: string;
  icon: string;
  bar: string;
}> = {
  wealth: {
    section: 'bg-gray-50 dark:bg-slate-950 border-emerald-500/10',
    eyebrow: 'text-emerald-600 dark:text-emerald-400',
    headline: 'from-emerald-500 to-cyan-500',
    tileBorder: 'border-emerald-500/20',
    tileGlow: 'hover:shadow-[0_0_50px_rgba(16,185,129,0.28)]',
    icon: 'text-emerald-500 dark:text-emerald-400',
    bar: 'from-emerald-400 to-cyan-500',
  },
  advisor: {
    section: 'bg-slate-950 dark:bg-black text-white border-violet-500/20',
    eyebrow: 'text-violet-300',
    headline: 'from-violet-400 via-fuchsia-400 to-cyan-400',
    tileBorder: 'border-violet-500/25',
    tileGlow: 'hover:shadow-[0_0_50px_rgba(139,92,246,0.35)]',
    icon: 'text-violet-400',
    bar: 'from-violet-400 to-fuchsia-500',
  },
  business: {
    section: 'bg-white dark:bg-slate-950 border-cyan-500/10',
    eyebrow: 'text-cyan-600 dark:text-cyan-400',
    headline: 'from-cyan-500 to-blue-500',
    tileBorder: 'border-cyan-500/20',
    tileGlow: 'hover:shadow-[0_0_50px_rgba(6,182,212,0.28)]',
    icon: 'text-cyan-500 dark:text-cyan-400',
    bar: 'from-cyan-400 to-blue-500',
  },
};

/**
 * Reusable persona block for FeaturesPage (Wealth, Advisor).
 * Editorial layout — big headline, 6 tiles in a 3-col grid.
 */
const PersonaFeatureBlock: React.FC<{ block: PersonaBlock }> = ({ block }) => {
  const isDark = block.persona === 'advisor';
  const t = theme[block.persona];
  return (
    <section
      className={`relative py-24 sm:py-32 border-t ${t.section}`}
      style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          className={`text-[11px] font-mono uppercase tracking-[0.28em] ${t.eyebrow} mb-6`}
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          — {block.eyebrow}
        </div>
        <h2
          className={`font-extrabold tracking-tight leading-[0.95] max-w-4xl ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}
          style={{ fontSize: 'clamp(2.25rem, 5.5vw, 5rem)' }}
        >
          <span className={`text-transparent bg-clip-text bg-gradient-to-r ${t.headline}`}>
            {block.headline}
          </span>
        </h2>
        <p className={`mt-6 max-w-2xl text-lg ${isDark ? 'text-slate-300' : 'text-gray-600 dark:text-slate-300'} leading-relaxed`}>
          {block.subhead}
        </p>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {block.tiles.map((tile, i) => (
            <div
              key={tile.title}
              className={`group relative overflow-hidden rounded-2xl border ${t.tileBorder} ${isDark ? 'bg-white/[0.02]' : 'bg-white dark:bg-slate-900/60'} backdrop-blur-sm p-7 transition-all duration-500 hover:-translate-y-1 ${t.tileGlow} animate-fade-up animate-fade-up-${(i % 5) + 1}`}
            >
              <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${t.bar} opacity-60 group-hover:opacity-100 transition-opacity`} />
              <div className={`text-5xl font-light ${t.icon} mb-5 leading-none`}>{tile.icon}</div>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{tile.title}</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-gray-600 dark:text-slate-400'}`}>{tile.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PersonaFeatureBlock;
